/*
  Finder traffic-source attribution.

  Resolves the visitor's traffic source once on the first page load (this
  script is loaded site-wide), persists it to sessionStorage, and exposes it
  via window.DongarAttribution so the site can (a) stamp each Amazon outbound
  click to a Dongar ASIN with the matching Amazon Attribution tag and (b)
  record the source on every event written to Supabase (finder and product
  pages). See the "Finder -> Amazon Attribution" handover.

  Traffic source is read from utm_source, else the short `src` param (which
  survives in-app webviews that strip UTMs, e.g. Reddit), else the referrer.

  Source -> bucket mapping:
    google / adwords / google_cpc          -> finder_google
    meta / facebook / fb / ig / instagram  -> finder_meta
    reddit / redd.it                       -> finder_reddit
    organic search referrer, no utm        -> finder_organic
    no utm, no referrer (typed / direct)   -> finder_direct
    email / affiliate / anything else      -> finder_other
  Fallback when nothing resolves: finder_organic (most finder traffic is organic).
*/
(function () {
  'use strict';

  var STORAGE_KEY = 'dongar_finder_attribution';
  var FALLBACK_BUCKET = 'finder_organic';

  /* maas_adg ID per bucket, from the "Finder Outclicks" Amazon Attribution
     campaign (one ad group per bucket). These drive Brand Referral Bonus. */
  var BUCKET_TAG = {
    finder_google:  '606079650FE9393A66DC4235D60E064A',
    finder_meta:    '01D9823A5D989C89A4660759F799A4D8',
    finder_reddit:  '2F53AAF7FD88CB823CA2FE3EFF709C61',
    finder_organic: '12B2D28B79422A6F764290BE4509427E',
    finder_direct:  '90E4FEFC4785D3F42B28A2949674FCF4',
    finder_other:   'A329449E38684928DC72E88F022A1AFD',
    /* Fit Finder A/B test: ALL v2_challenger outbound Amazon clicks land in
       this one ad group regardless of traffic source (handoff doc §5).
       Real "finder_challenger" ad group, Finder Outclicks campaign,
       created 2026-09-24. */
    finder_challenger: 'E2E5B32B6E54CB5894BC0F102DED0DC9'
  };

  var SEARCH_ENGINES = ['google', 'bing', 'yahoo', 'duckduckgo', 'ecosia', 'baidu', 'yandex', 'startpage', 'brave', 'aol', 'ask'];

  function clean(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9_.\- ]/g, '').trim().slice(0, 64);
  }

  function bucketForUtm(source, medium) {
    var s = source + ' ' + (medium || '');
    if (s.indexOf('google') > -1 || s.indexOf('adwords') > -1) return 'finder_google';
    if (s.indexOf('meta') > -1 || s.indexOf('facebook') > -1 || s.indexOf('instagram') > -1 ||
        /(^| )fb( |$)/.test(s) || /(^| )ig( |$)/.test(s)) return 'finder_meta';
    if (s.indexOf('reddit') > -1 || s.indexOf('redd.it') > -1) return 'finder_reddit';
    /* email / klaviyo / affiliate / everything else */
    return 'finder_other';
  }

  function referrerHost() {
    try {
      var ref = document.referrer || '';
      if (!ref) return '';
      return new URL(ref).hostname.toLowerCase().replace(/^www\./, '');
    } catch (e) { return ''; }
  }

  function matchedSearchEngine(host) {
    for (var i = 0; i < SEARCH_ENGINES.length; i++) {
      if (host.indexOf(SEARCH_ENGINES[i]) > -1) return SEARCH_ENGINES[i];
    }
    return '';
  }

  function resolve() {
    var utmSource = '', utmMedium = '', utmCampaign = '', utmContent = '', srcParam = '', fbclid = '';
    try {
      var p = new URLSearchParams(window.location.search);
      utmSource = clean(p.get('utm_source'));
      utmMedium = clean(p.get('utm_medium'));
      utmCampaign = clean(p.get('utm_campaign'));
      utmContent = clean(p.get('utm_content'));
      srcParam = clean(p.get('src'));
      /* Presence only — Meta appends fbclid on every FB/IG click and its
         in-app browser keeps it even when it strips utm_*. Last-resort Meta
         signal so paid clicks land in finder_meta, not finder_direct. */
      fbclid = p.get('fbclid') || '';
    } catch (e) {}

    /* utm_source wins; else the short `src` param (survives webviews that
       strip UTMs, e.g. Reddit's in-app browser); else referrer; else direct. */
    var explicitSource = utmSource || srcParam;

    var source, bucket;

    if (explicitSource) {
      source = explicitSource;
      bucket = bucketForUtm(explicitSource, utmMedium);
    } else if (fbclid) {
      /* UTM/src both stripped but the Meta click id survived -> it's Meta. */
      source = 'facebook';
      bucket = 'finder_meta';
    } else {
      var host = referrerHost();
      var engine = host ? matchedSearchEngine(host) : '';
      if (!host || host === (window.location.hostname || '').replace(/^www\./, '')) {
        /* No referrer, or internal navigation -> original source unknown. */
        source = 'direct';
        bucket = 'finder_direct';
      } else if (engine) {
        source = engine;
        bucket = 'finder_organic';
      } else if (host.indexOf('facebook') > -1 || host.indexOf('instagram') > -1) {
        source = host.indexOf('instagram') > -1 ? 'instagram' : 'facebook';
        bucket = 'finder_meta';
      } else if (host.indexOf('reddit') > -1 || host.indexOf('redd.it') > -1) {
        source = 'reddit';
        bucket = 'finder_reddit';
      } else {
        source = host.slice(0, 64);
        bucket = 'finder_other';
      }
    }

    if (!BUCKET_TAG[bucket]) bucket = FALLBACK_BUCKET;

    return {
      source: source || 'direct',
      bucket: bucket,
      utm_source: utmSource || null,
      utm_medium: utmMedium || null,
      utm_campaign: utmCampaign || null,
      utm_content: utmContent || null
    };
  }

  function load() {
    try {
      var raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (parsed && parsed.bucket && BUCKET_TAG[parsed.bucket]) return parsed;
    } catch (e) {}
    return null;
  }

  function save(attr) {
    try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attr)); } catch (e) {}
  }

  /* First-touch within the session, except a landing with explicit utm_source
     or src params always re-attributes (a fresh ad click should win). */
  function init() {
    var hasExplicit = false;
    try {
      var p = new URLSearchParams(window.location.search);
      hasExplicit = !!(p.get('utm_source') || p.get('src') || p.get('fbclid'));
    } catch (e) {}
    if (!hasExplicit) {
      var stored = load();
      if (stored) return stored;
    }
    var attr = resolve();
    save(attr);
    return attr;
  }

  var current = init();

  /* Fit Finder A/B test: the v2_challenger page stamps window.DongarPageVariant
     (see device-compatibility.liquid) before this script loads. When present,
     every outbound Amazon click from that page is tagged finder_challenger
     regardless of traffic source, overriding the normal source-based bucket —
     see handoff doc §5 ("simple approach"). No effect on any other page. */
  function bucket() {
    try { if (window.DongarPageVariant === 'v2_challenger') return 'finder_challenger'; } catch (e) {}
    return (current && current.bucket) || FALLBACK_BUCKET;
  }
  function source() { return (current && current.source) || 'direct'; }

  /* Click-capture wrap: route the final Amazon URL through the attribution
     redirect on inventory.dongar.tech, which records the click (gclid/gbraid/
     wbraid, GA4 client id, Meta ids, UTM, traffic bucket as `placement`) and
     302s to the same per-market listing URL with the same maas tag — the
     `tag` param carries this bucket's ad-group ID through the redirect.
     Fail-open: anything unexpected returns the direct Amazon URL. */
  var GO_BASE = 'https://inventory.dongar.tech/go/amazon';
  function goWrap(amazonUrl, marketplace) {
    try {
      var a = /\/dp\/([A-Z0-9]{10})/i.exec(amazonUrl);
      if (!a) return amazonUrl; /* no ASIN -> can't reconstruct; go direct */
      var t = /maas=(maas_adg_[A-Za-z0-9_]+)/.exec(amazonUrl);
      var q = 'asin=' + a[1]
        + '&mkt=' + encodeURIComponent(String(marketplace || 'US').toUpperCase())
        + '&placement=' + encodeURIComponent(bucket());
      if (t) q += '&tag=' + encodeURIComponent(t[1]);
      /* Forward the session's UTMs so the click row can be split by campaign /
         creative (the redirect stores utm_* verbatim). Without this every
         finder_meta click reports utm_* = null and the Amazon halo can only
         be read at bucket level, not per Meta campaign or ad. */
      var c = current || {};
      var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
      for (var i = 0; i < UTM_KEYS.length; i++) {
        if (c[UTM_KEYS[i]]) q += '&' + UTM_KEYS[i] + '=' + encodeURIComponent(c[UTM_KEYS[i]]);
      }
      return GO_BASE + '?' + q;
    } catch (e) { return amazonUrl; }
  }

  /* Build a per-channel Amazon Attribution URL for an ASIN or any amazon /dp/
     URL. Drops any existing affiliate tag and lands on the bare ASIN page with
     the maas params for the resolved bucket. If no ASIN is present the params
     are appended to the given URL rather than dropped. */
  function amazonAttributionUrl(asinOrUrl) {
    if (!asinOrUrl) return '';
    var asin = '';
    var m = /\/dp\/([A-Z0-9]{10})/.exec(asinOrUrl);
    if (m) asin = m[1];
    else if (/^[A-Z0-9]{10}$/.test(asinOrUrl)) asin = asinOrUrl;
    var tagId = BUCKET_TAG[bucket()] || BUCKET_TAG[FALLBACK_BUCKET];
    var params = 'maas=maas_adg_' + tagId + '_afap_abs&ref_=aa_maas&tag=maas';
    if (asin) return goWrap('https://www.amazon.com/dp/' + asin + '?' + params, 'US');
    return asinOrUrl + (asinOrUrl.indexOf('?') > -1 ? '&' : '?') + params;
  }

  /* Per-marketplace source-tag families. Amazon Attribution tags are minted
     per Brand Registry region, so each marketplace needs its own set; only US
     exists today. A marketplace without an entry keeps the base tag already
     embedded in its listing URL (amazon_listings.amazon_url carries the
     marketplace's maas_tag_base). When a region's tags are minted, add its
     bucket map here and the per-source swap turns on with no other changes. */
  var MARKET_BUCKET_TAG = {
    US: BUCKET_TAG
  };

  /* Marketplace-aware variant of amazonAttributionUrl for the geo-aware buy
     cascade. US routes through amazonAttributionUrl unchanged (byte-identical
     URLs — hard no-regression constraint). Other marketplaces keep the
     listing URL's domain: if that marketplace has a source-tag family the
     maas ID is swapped in place, otherwise the URL is returned untouched. */
  function amazonAttributionUrlFor(asinOrUrl, marketplace) {
    var mkt = String(marketplace || 'US').toUpperCase();
    if (mkt === 'US') return amazonAttributionUrl(asinOrUrl);
    if (!asinOrUrl) return '';
    var tags = MARKET_BUCKET_TAG[mkt];
    var tagId = tags && (tags[bucket()] || tags[FALLBACK_BUCKET]);
    /* No per-source tag family for this marketplace: the redirect resolves
       the listing URL (with its baked maas_tag_base) from amazon_listings. */
    if (!tagId) return goWrap(asinOrUrl, mkt);
    if (/maas=maas_adg_[A-Za-z0-9]+/.test(asinOrUrl)) {
      return goWrap(
        asinOrUrl.replace(/maas=maas_adg_[A-Za-z0-9]+/, 'maas=maas_adg_' + tagId),
        mkt
      );
    }
    return goWrap(
      asinOrUrl + (asinOrUrl.indexOf('?') > -1 ? '&' : '?')
        + 'maas=maas_adg_' + tagId + '_afap_abs&ref_=aa_maas&tag=maas',
      mkt
    );
  }

  window.DongarAttribution = {
    get: function () { return current; },
    source: source,
    bucket: bucket,
    tags: BUCKET_TAG,
    amazonAttributionUrl: amazonAttributionUrl,
    amazonAttributionUrlFor: amazonAttributionUrlFor
  };
})();
